import { PLATFORM_PERMISSIONS, PLATFORM_USER_ROLES, USER_ROLES } from '../constants/roles';

const ELEVATED_ROLES = [USER_ROLES.SUPER_ADMIN, USER_ROLES.STATE_ADMIN];
export const TENANT_SOLUTION_MODES = {
  FULL_SUITE: 'FULL_SUITE',
  BILLABLE_ONLY: 'BILLABLE_ONLY',
  TRACKING_ONLY: 'TRACKING_ONLY',
};

export const getTenantSolutionMode = (user) => {
  const normalized = String(user?.solutionMode || '').trim().toUpperCase();

  if (normalized === TENANT_SOLUTION_MODES.BILLABLE_ONLY) {
    return TENANT_SOLUTION_MODES.BILLABLE_ONLY;
  }

  if (normalized === TENANT_SOLUTION_MODES.TRACKING_ONLY) {
    return TENANT_SOLUTION_MODES.TRACKING_ONLY;
  }

  return TENANT_SOLUTION_MODES.FULL_SUITE;
};

export const formatTenantSolutionMode = (value) => {
  switch (getTenantSolutionMode({ solutionMode: value })) {
    case TENANT_SOLUTION_MODES.BILLABLE_ONLY:
      return 'Billable Only';
    case TENANT_SOLUTION_MODES.TRACKING_ONLY:
      return 'Distribution Only';
    default:
      return 'Full Suite';
  }
};

export const canAccessBillableModule = (user) => getTenantSolutionMode(user) !== TENANT_SOLUTION_MODES.TRACKING_ONLY;

export const canAccessTrackingModule = (user) => getTenantSolutionMode(user) !== TENANT_SOLUTION_MODES.BILLABLE_ONLY;

export const canUseExternalBillImport = (user) => getTenantSolutionMode(user) === TENANT_SOLUTION_MODES.TRACKING_ONLY;

export const canUseBulkBillImport = (user) => canAccessTrackingModule(user);

export const getScopedTenantAccess = (tenantAccess, tenantId) => {
  if (!tenantAccess) {
    return null;
  }

  const normalizedTenantId = String(tenantId || '').trim();
  const accessTenantId = String(tenantAccess?.tenantId || '').trim();

  if (!normalizedTenantId) {
    return tenantAccess;
  }

  if (!accessTenantId) {
    return null;
  }

  return accessTenantId === normalizedTenantId ? tenantAccess : null;
};

export const buildTenantAccessFallback = (user, tenantProfile) => {
  if (!tenantProfile) {
    return user;
  }

  return {
    ...(user || {}),
    ...tenantProfile,
    tenantId: tenantProfile?.tenantId || tenantProfile?._id || user?.tenantId,
    role: user?.role,
    permissions: user?.permissions,
    solutionMode: tenantProfile?.solutionMode || user?.solutionMode,
  };
};

export const getTenantHomeRoute = (user) => {
  if (!canUseExternalBillImport(user)) {
    return '/dashboard/profile';
  }

  if (canViewDistributionReports(user)) {
    return '/distributions/dashboard';
  }

  if (canReviewDistributions(user)) {
    return '/distributions/flagged';
  }

  return '/dashboard/profile';
};

export const hasRole = (user, roles = []) => {
  if (!user || !Array.isArray(roles)) {
    return false;
  }

  return roles.includes(user.role);
};

export const isRootSuperAdmin = (user) => user?.role === USER_ROLES.SUPER_ADMIN;

export const isPlatformUser = (user) => PLATFORM_USER_ROLES.includes(user?.role);

const PLATFORM_PERMISSION_DEFAULTS = PLATFORM_PERMISSIONS.reduce((acc, permission) => {
  acc[permission.key] = true;
  return acc;
}, {});

const PLATFORM_ROLE_DEFAULT_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: PLATFORM_PERMISSION_DEFAULTS,
  [USER_ROLES.PLATFORM_ADMIN]: PLATFORM_PERMISSION_DEFAULTS,
  [USER_ROLES.PLATFORM_REPORT_USER]: {
    canViewOwnerReports: true,
    canRunOwnerReports: true,
  },
};

export const resolvePlatformPermissions = (user) => {
  if (!isPlatformUser(user)) {
    return {};
  }

  if (isRootSuperAdmin(user)) {
    return { ...PLATFORM_PERMISSION_DEFAULTS };
  }

  const permissions = {
    ...(PLATFORM_ROLE_DEFAULT_PERMISSIONS[user.role] || {}),
    ...(user?.permissions || {}),
  };

  if (permissions.canViewOwnerReports === false) {
    permissions.canRunOwnerReports = false;
  }

  return permissions;
};

export const hasPlatformPermission = (user, permissionKey) => {
  if (!user || !permissionKey || !isPlatformUser(user)) {
    return false;
  }

  if (isRootSuperAdmin(user)) {
    return true;
  }

  return resolvePlatformPermissions(user)?.[permissionKey] === true;
};

export const hasAnyPlatformPermission = (user, permissionKeys = []) => {
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
    return false;
  }

  return permissionKeys.some((permission) => hasPlatformPermission(user, permission));
};

export const canManagePlatformUsers = (user) => hasPlatformPermission(user, 'canManagePlatformUsers');

export const canManagePlatformTenants = (user) => hasPlatformPermission(user, 'canManageTenants');

export const canViewPlatformAnalytics = (user) => hasPlatformPermission(user, 'canViewPlatformAnalytics');

export const canViewPlatformAuditLogs = (user) => hasPlatformPermission(user, 'canViewPlatformAuditLogs');

export const canViewOwnerReports = (user) => hasPlatformPermission(user, 'canViewOwnerReports');

export const canRunOwnerReports = (user) => hasPlatformPermission(user, 'canRunOwnerReports');

export const canManageOwnerReportConfigs = (user) => hasPlatformPermission(user, 'canManageOwnerReportConfigs');

export const canManageTenantCommercialTerms = (user) => hasPlatformPermission(user, 'canManageTenantCommercialTerms');

export const canViewOwnerReportConfigs = (user) => canViewOwnerReports(user) || canManageOwnerReportConfigs(user);

export const canAccessOwnerReportsWorkspace = (user) => hasAnyPlatformPermission(user, [
  'canViewOwnerReports',
  'canManageOwnerReportConfigs',
  'canManageTenantCommercialTerms',
]);

export const getPlatformHomeRoute = (user) => {
  if (!isPlatformUser(user)) {
    return '/tenancy_profile';
  }

  if (canViewPlatformAnalytics(user)) {
    return '/super-admin/dashboard';
  }

  if (canManagePlatformTenants(user)) {
    return '/super-admin/tenants';
  }

  if (canAccessOwnerReportsWorkspace(user)) {
    return '/super-admin/owner-reports';
  }

  if (canManagePlatformUsers(user)) {
    return '/super-admin/platform-users';
  }

  if (canViewPlatformAuditLogs(user)) {
    return '/super-admin/audit-logs';
  }

  return '/super-admin/access';
};

export const hasPermission = (user, permissionKey) => {
  if (!user || !permissionKey) {
    return false;
  }

  if (ELEVATED_ROLES.includes(user.role)) {
    return true;
  }

  return user?.permissions?.[permissionKey] === true;
};

export const hasAnyPermission = (user, permissionKeys = []) => {
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
    return false;
  }

  return permissionKeys.some((permission) => hasPermission(user, permission));
};

export const canManageUsers = (user) => hasPermission(user, 'canManageUsers');

export const canManageTenantUsers = (user) => canManageUsers(user);

export const canAssignUserRoles = (user) => hasPermission(user, 'canAssignRoles');

export const canManageUserPermissionOverrides = (user) => hasPermission(user, 'canManageUserPermissions');

export const canSuspendTenantUsers = (user) => hasPermission(user, 'canSuspendUsers');

export const canAssignTenantLgaScope = (user) => hasPermission(user, 'canManageUsers');

export const canOpenDistributionWorkspace = (user) => hasAnyPermission(user, [
  'canRecordDistribution',
  'canViewDistributionDashboard',
  'canReviewDistributionFlags',
  'canVerifyDistributions',
  'canAddEnforcementVisits',
]) && canAccessTrackingModule(user);

export const canViewDistributionReports = (user) => canAccessTrackingModule(user) && hasPermission(user, 'canViewDistributionDashboard');

export const canExportDistributionReports = (user) => canAccessTrackingModule(user) && hasPermission(user, 'canExportReports');

export const canAccessDistributionDashboard = (user) => canOpenDistributionWorkspace(user);

export const canReviewDistributions = (user) => canAccessTrackingModule(user) && hasPermission(user, 'canReviewDistributionFlags');

export const canManageDistributionClaims = (user) => canReviewDistributions(user);

export const canManageTenantSettings = (user) => hasPermission(user, 'canManageTenantSettings');

export const canManageLocations = (user) => hasPermission(user, 'canManageLocations');

export const canManageRevenueHeads = (user) => hasPermission(user, 'canManageRevenueHeads');

export const canManageDemandNoticeCategories = (user) => hasPermission(user, 'canManageDemandNoticeCategories');

export const canGenerateDemandNotices = (user) => hasPermission(user, 'canGenerateDemandNotices');

export const canExportDemandNoticeData = (user) => canAccessBillableModule(user) && hasPermission(user, 'canExportDemandNotices');

export const canPrintDemandNotices = (user) => canAccessBillableModule(user) && hasAnyPermission(user, [
  'canGenerateDemandNotices',
  'canExportDemandNotices',
]);

export const canDownloadDemandNoticeReceipts = (user) => canPrintDemandNotices(user);

export const canViewFinanceReports = (user) => hasPermission(user, 'canViewReports');

export const canViewAuditLogs = (user) => hasPermission(user, 'canViewAuditLogs');

export const canRecordDistribution = (user) => canAccessTrackingModule(user) && hasPermission(user, 'canRecordDistribution');

export const canViewBulkBillImportHistory = (user) => canUseBulkBillImport(user) && canViewDistributionReports(user);

export const canManageBulkBillImport = (user) => canUseBulkBillImport(user) && canRecordDistribution(user);
